// components/Button.tsx
"use client";

import React from "react";
import { useTranslation } from "react-i18next";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string; // This should be a translation key, not plain text
  /** When true, shows a small inline spinner and optional loading label; button is non-interactive. */
  loading?: boolean;
  /** Translation key shown while loading; falls back to `label` if omitted. */
  loadingLabel?: string;
  variant?: "primary" | "secondary" | "error" | "light";
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  baseAppend?: string;
  icon?: React.ReactNode;
  widthAppend?: string;
}

const Button: React.FC<ButtonProps> = ({
  label,
  loading = false,
  loadingLabel,
  variant = "primary",
  fullWidth = false,
  className = "",
  disabled = false,
  baseAppend = "",
  icon,
  widthAppend = "",
  ...rest
}) => {
  const { t } = useTranslation("common");

  const isDisabled = disabled || loading;

  // Taller tap target on mobile (py-2.5 ≈ 44px); reverts to compact padding from sm up.
  let baseStyles = isDisabled
    ? "px-6 py-2.5 sm:py-2 xl:py-3 rounded-[20px] font-medium transition-all duration-200 ease-in border border-white cursor-not-allowed text-[14px] md:text-[16px]"
    : "px-6 py-2.5 sm:py-2 xl:py-3 rounded-[20px] font-medium transition-all duration-200 ease-in border border-white cursor-pointer text-[14px] md:text-[16px]";

  if (baseAppend) {
    baseStyles += ` ${baseAppend}`;
  }

  let variantStyles = "";
  if (variant === "primary") {
    variantStyles = isDisabled
      ? "bg-app-black text-white hover:bg-dark-black opacity-50"
      : "bg-app-black text-white hover:bg-dark-black";
  } else if (variant === "secondary") {
    variantStyles = isDisabled
      ? "bg-[#2D2D2D] text-white hover:bg-black opacity-50"
      : "bg-[#2D2D2D] text-white hover:bg-black";
  } else if (variant === "error") {
    variantStyles = isDisabled
      ? "bg-app-red hover:bg-app-dark-red text-white opacity-50"
      : "bg-app-red hover:bg-app-dark-red text-white";
  } else if (variant === "light") {
    variantStyles = isDisabled
      ? "bg-gray-100 text-app-black hover:bg-gray-200 opacity-50 border border-gray-200"
      : "bg-gray-100 text-app-black hover:bg-gray-200 border border-gray-200";
  }

  const widthStyle = fullWidth
    ? "w-full"
    : widthAppend
      ? widthAppend
      : "max-w-[264px] w-max md:min-w-max";
  const combinedClasses = `${baseStyles} ${variantStyles} ${widthStyle} `;

  const customCombinedClasses = `${className} ${variantStyles}`;

  const spinnerBorderClass =
    variant === "light"
      ? "border-gray-600 border-t-transparent"
      : "border-white border-t-transparent";

  const textKey = loading && loadingLabel ? loadingLabel : label;

  return (
    <button
      className={className ? customCombinedClasses : combinedClasses}
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      <div className="flex items-center justify-center gap-2">
        {loading && (
          <span
            className={`inline-block size-3.5 shrink-0 rounded-full border-2 ${spinnerBorderClass} animate-spin`}
            aria-hidden
          />
        )}
        {!loading && icon && icon}
        {t(textKey)}
      </div>
    </button>
  );
};

export default Button;
